import axios, { type AxiosInstance } from "axios";
import { getAuth } from "firebase/auth";

import React, {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
} from "react";

import { useFirebase } from "../firebase/useFirebase";

const API_BASE_URL = "https://gateway.ingredifind.com";

const createAxiosClient = (): AxiosInstance =>
  axios.create({
    baseURL: API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

export type AxiosInstances = {
  clientWithAuth: AxiosInstance;
  clientWithoutAuth: AxiosInstance;
};

export const createAxiosInstances = (): AxiosInstances => ({
  // interceptors are added by the wrapper providers below
  // the providers are added by the respective layout components
  clientWithAuth: createAxiosClient(),
  clientWithoutAuth: createAxiosClient(),
});

/** Default singleton — every app shares the same gateway URL. */
export const axiosInstances = createAxiosInstances();

export const AxiosClientContext = createContext<AxiosInstances>(axiosInstances);

/**
 * Provider that adds the auth token to the request.
 * This is used to authenticate all requests.
 *
 * This is added by the AuthenticatedLayout
 */
export const AxiosAuthTokenProvider = ({ children }: PropsWithChildren) => {
  const { clientWithAuth } = useContext(AxiosClientContext);
  const firebase = useFirebase();
  const auth = getAuth(firebase.firebaseApp);

  useEffect(() => {
    const authInterceptorId = clientWithAuth.interceptors.request.use(
      async (config) => {
        const token = await auth.currentUser?.getIdToken();
        if (!token) {
          throw new Error("No token found");
        }
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      }
    );
    return () => {
      // FIXME ensure that we eject only when the restaurantUuid changes.
      clientWithAuth.interceptors.request.eject(authInterceptorId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.currentUser?.uid]);

  return <>{children}</>;
};

/**
 * Helper hook to get the correct axios client.
 */
export const useAxiosClient = ({ withAuth }: { withAuth: boolean }) => {
  const { clientWithAuth, clientWithoutAuth } = useContext(AxiosClientContext);
  if (withAuth) {
    return clientWithAuth;
  }
  return clientWithoutAuth;
};
